(() => {
  if (customElements.get('tidal-header')) return;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HOVER_OPEN_DELAY = 90;
  const HOVER_CLOSE_DELAY = 220;

  class TidalHeader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.mobileToggle = this.querySelector('[data-tidal-menu-toggle]');
      this.mobilePanel = this.querySelector('[data-tidal-mobile-panel]');
      this.searchPanel = this.querySelector('[data-tidal-search-panel]');
      this.mobileClosers = Array.from(this.querySelectorAll('[data-tidal-menu-close]'));
      this.disclosures = Array.from(this.querySelectorAll('[data-harbor-disclosure]'));
      this.mobileOpening = false;
      this.searchOpening = false;
      this.mobileOpenFrame = null;
      this.mobileTrapFrame = null;
      this.searchOpenFrame = null;
      this.searchTrapFrame = null;
      this.hoverOpenTimer = null;
      this.hoverCloseTimer = null;
      this.hoverListeners = [];
      this.scrollThreshold = Number.parseInt(this.dataset.scrollThreshold, 10);
      if (!Number.isFinite(this.scrollThreshold)) this.scrollThreshold = 12;

      this.onClick = this.handleClick.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.scheduleInk.bind(this);
      this.inkFrame = null;
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.handleBlockDeselect.bind(this);
      this.onPointerChange = this.bindHoverIntent.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      finePointer.addEventListener?.('change', this.onPointerChange);
      this.bindHoverIntent();
      if (this.dataset.sticky === 'true') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        document.addEventListener('shopify:section:load', this.onResize);
        window.addEventListener('load', this.onResize, { once: true });
        this.handleScroll();
      }
    }

    disconnectedCallback() {
      this.closeMobile({ restore: false, immediate: true });
      this.closeSearch({ restore: false, immediate: true });
      this.closeHarbors({ restore: false });
      this.unbindHoverIntent();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      finePointer.removeEventListener?.('change', this.onPointerChange);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:section:load', this.onResize);
      window.cancelAnimationFrame(this.inkFrame);
      window.clearTimeout(this.inkSettleTimer);
      this.initialized = false;
    }

    /* ---- panel lookup: panels live outside the pill so they are never clipped ---- */

    panelFor(disclosure) {
      const toggle = disclosure?.querySelector('[data-harbor-toggle]');
      const id = toggle?.getAttribute('aria-controls');
      return { toggle, panel: id ? this.querySelector(`#${CSS.escape(id)}`) : null };
    }

    disclosureFor(panel) {
      if (!panel?.id) return null;
      return this.querySelector(`[data-harbor-toggle][aria-controls="${CSS.escape(panel.id)}"]`)?.closest('[data-harbor-disclosure]') || null;
    }

    /* ---- hover intent (fine pointers only, merchant setting) ---- */

    bindHoverIntent() {
      this.unbindHoverIntent();
      if (this.dataset.hoverOpen !== 'true' || !finePointer.matches) return;
      this.disclosures.forEach((disclosure) => {
        const { panel } = this.panelFor(disclosure);
        const enter = () => this.scheduleHoverOpen(disclosure);
        const leave = () => this.scheduleHoverClose();
        [disclosure, panel].filter(Boolean).forEach((node) => {
          node.addEventListener('pointerenter', enter);
          node.addEventListener('pointerleave', leave);
          this.hoverListeners.push([node, enter, leave]);
        });
      });
    }

    unbindHoverIntent() {
      window.clearTimeout(this.hoverOpenTimer);
      window.clearTimeout(this.hoverCloseTimer);
      this.hoverListeners.forEach(([node, enter, leave]) => {
        node.removeEventListener('pointerenter', enter);
        node.removeEventListener('pointerleave', leave);
      });
      this.hoverListeners = [];
    }

    scheduleHoverOpen(disclosure) {
      window.clearTimeout(this.hoverCloseTimer);
      window.clearTimeout(this.hoverOpenTimer);
      if (disclosure.classList.contains('is-open')) return;
      this.hoverOpenTimer = window.setTimeout(() => {
        this.closeHarbors({ restore: false });
        this.openHarbor(disclosure);
      }, HOVER_OPEN_DELAY);
    }

    scheduleHoverClose() {
      window.clearTimeout(this.hoverOpenTimer);
      window.clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = window.setTimeout(() => this.closeHarbors({ restore: false }), HOVER_CLOSE_DELAY);
    }

    /* ---- events ---- */

    handleClick(event) {
      const menuToggle = event.target.closest('[data-tidal-menu-toggle]');
      if (menuToggle && this.contains(menuToggle)) {
        event.preventDefault();
        this.openMobile();
        return;
      }

      const menuClose = event.target.closest('[data-tidal-menu-close]');
      if (menuClose && this.contains(menuClose)) {
        event.preventDefault();
        this.closeMobile();
        return;
      }

      const searchLauncher = event.target.closest('[data-predictive-search-launcher]');
      if (searchLauncher && this.contains(searchLauncher)) {
        event.preventDefault();
        const searchReturnTarget = this.mobilePanel?.classList.contains('is-open') ? this.mobileToggle : searchLauncher;
        this.closeMobile({ restore: false, immediate: true });
        this.openSearch(searchLauncher, searchReturnTarget);
        return;
      }

      const searchClose = event.target.closest('[data-tidal-search-close]');
      if (searchClose && this.contains(searchClose)) {
        event.preventDefault();
        this.closeSearch();
        return;
      }

      if (event.target.closest('[data-cart-drawer-open]') && this.mobilePanel?.classList.contains('is-open')) {
        this.closeMobile({ restore: false, immediate: true });
      }

      const harborToggle = event.target.closest('[data-harbor-toggle]');
      if (harborToggle && this.contains(harborToggle)) {
        event.preventDefault();
        window.clearTimeout(this.hoverOpenTimer);
        window.clearTimeout(this.hoverCloseTimer);
        const disclosure = harborToggle.closest('[data-harbor-disclosure]');
        const shouldOpen = harborToggle.getAttribute('aria-expanded') !== 'true';
        this.closeHarbors({ restore: false });
        if (!shouldOpen) return;
        this.openHarbor(disclosure);
        // A keyboard activation (detail 0) moves focus into the panel so the
        // links are the next Tab stops; Escape returns focus to the tab.
        if (event.detail === 0) this.focusPanel(disclosure);
      }
    }

    focusPanel(disclosure) {
      const { panel } = this.panelFor(disclosure);
      const target = panel && (window.Reefline?.getFocusable?.(panel)?.[0] || panel.querySelector('a, button'));
      if (target) target.focus({ preventScroll: true });
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) this.closeHarbors({ restore: false });
    }

    handleFocusOut(event) {
      const next = event.relatedTarget;
      if (next && this.contains(next)) return;
      if (!this.querySelector('[data-harbor-disclosure].is-open')) return;
      window.setTimeout(() => {
        if (!this.contains(document.activeElement)) this.closeHarbors({ restore: false });
      }, 0);
    }

    handleKeyDown(event) {
      if (event.key === 'ArrowDown') {
        const toggle = event.target.closest?.('[data-harbor-toggle]');
        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          const disclosure = toggle.closest('[data-harbor-disclosure]');
          this.closeHarbors({ restore: false });
          this.openHarbor(disclosure);
          this.focusPanel(disclosure);
        }
        return;
      }
      if (event.key !== 'Escape') return;
      if (this.searchPanel?.classList.contains('is-open')) {
        event.preventDefault();
        this.closeSearch();
        return;
      }
      if (this.mobilePanel?.classList.contains('is-open')) {
        event.preventDefault();
        this.closeMobile();
        return;
      }
      const openDisclosure = this.querySelector('[data-harbor-disclosure].is-open');
      if (openDisclosure) {
        event.preventDefault();
        this.closeHarbors({ restore: true });
      }
    }

    handleScroll() {
      this.classList.toggle('is-scrolled', window.scrollY >= this.scrollThreshold);
      this.scheduleInk();
    }

    /* ---- adaptive ink: read what sits behind the pills and pick dark or light text plus a matching glass tint ---- */

    scheduleInk() {
      // Sample on the next frame, then once more after the scroll settles:
      // hit-testing can lag a programmatic jump by a frame.
      window.clearTimeout(this.inkSettleTimer);
      this.inkSettleTimer = window.setTimeout(() => this.updateInk(), 140);
      if (this.inkFrame !== null) return;
      this.inkFrame = window.requestAnimationFrame(() => {
        this.inkFrame = null;
        this.updateInk();
      });
    }

    toneAt(x, y) {
      if (!document.elementsFromPoint) return null;
      for (const element of document.elementsFromPoint(x, y)) {
        if (this.contains(element) || element.closest?.('.section-header, [data-tidal-mobile-panel], [data-tidal-search-panel], .cart-drawer-root, .product-float')) continue;
        if (element.matches?.('img, video, picture, canvas, svg.placeholder-svg')) {
          return element.closest('.product-media__item--contain') ? 'light' : 'dark';
        }
        // Section bands paint their colour on a pointer-events:none layer that
        // hit-testing skips, so read it from the shell explicitly.
        const band = element.matches?.('.section-shell') ? element.querySelector(':scope > .section-shell__background') : null;
        const tone = TidalHeader.toneOf(getComputedStyle(band || element).backgroundColor);
        if (tone) return tone;
      }
      return TidalHeader.toneOf(getComputedStyle(document.body).backgroundColor) || 'light';
    }

    static toneOf(color) {
      const match = /rgba?\(([^)]+)\)/.exec(color || '');
      if (!match) return null;
      const parts = match[1].split(/[\s,\/]+/).filter(Boolean).map(Number);
      if (parts.length >= 4 && parts[3] < .35) return null;
      const [r, g, b] = parts;
      const luminance = (.2126 * r + .7152 * g + .0722 * b) / 255;
      return luminance > .55 ? 'light' : 'dark';
    }

    updateInk() {
      if (!this.isConnected || this.classList.contains('is-menu-open')) return;
      // Each pill reads its own backdrop, so the nav pill over a photo and the
      // utilities pill over a snow buybox can disagree.
      this.querySelectorAll('.tidal-dock__glass').forEach((pill) => {
        const rect = pill.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const y = Math.max(2, Math.min(window.innerHeight - 2, rect.top + rect.height / 2));
        const votes = [.2, .5, .8].map((f) => this.toneAt(rect.left + rect.width * f, y)).filter(Boolean);
        if (!votes.length) return;
        const light = votes.filter((tone) => tone === 'light').length;
        const ink = light > votes.length / 2 ? 'dark' : 'light';
        if (pill.dataset.ink !== ink) pill.dataset.ink = ink;
      });
    }

    handleBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const panel = event.target.closest('[data-harbor-panel]');
      if (panel) this.openHarbor(this.disclosureFor(panel));
    }

    handleBlockDeselect(event) {
      if (this.contains(event.target)) this.closeHarbors({ restore: false });
    }

    /* ---- dropdown panels ---- */

    openHarbor(disclosure) {
      if (!disclosure) return;
      const { toggle, panel } = this.panelFor(disclosure);
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      disclosure.classList.add('is-open');
      this.classList.add('is-menu-open');
    }

    closeHarbors({ restore = false } = {}) {
      this.disclosures.forEach((disclosure) => {
        const { toggle, panel } = this.panelFor(disclosure);
        if (!toggle || !panel || toggle.getAttribute('aria-expanded') !== 'true') return;
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
        disclosure.classList.remove('is-open');
        if (restore) toggle.focus();
      });
      this.classList.remove('is-menu-open');
    }

    /* ---- mobile sheet ---- */

    openMobile() {
      if (!this.mobilePanel || !this.mobileToggle) return;
      if (this.mobileOpening || this.mobilePanel.classList.contains('is-open')) return;
      this.closeHarbors({ restore: false });
      this.mobileOpening = true;
      this.returnTarget = this.mobileToggle;
      this.mobilePanel.hidden = false;
      this.mobileToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('is-locked');
      this.mobileOpenFrame = window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.mobileOpening || this.mobilePanel.hidden) return;
        this.mobilePanel.classList.add('is-open');
        this.mobileTrapFrame = window.requestAnimationFrame(() => {
          if (!this.isConnected || !this.mobileOpening || !this.mobilePanel.classList.contains('is-open')) return;
          this.mobileTrap = window.Reefline?.activateModal?.(this.mobilePanel, {
            launcher: this.mobileToggle,
            initialFocus: `#MobileNavigationTitle-${this.dataset.sectionId}`
          });
        });
      });
    }

    closeMobile({ restore = true, immediate = false } = {}) {
      if (!this.mobilePanel || !this.mobileToggle) return;
      this.mobileOpening = false;
      window.cancelAnimationFrame(this.mobileOpenFrame);
      window.cancelAnimationFrame(this.mobileTrapFrame);
      this.mobileOpenFrame = null;
      this.mobileTrapFrame = null;
      this.mobilePanel.classList.remove('is-open');
      this.mobileToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-locked');
      window.Reefline?.deactivateModal?.(this.mobilePanel, { restore });
      this.mobileTrap = null;
      const hide = () => {
        if (!this.mobilePanel.classList.contains('is-open')) this.mobilePanel.hidden = true;
      };
      if (immediate || reducedMotion.matches) hide();
      else window.setTimeout(hide, 300);
    }

    /* ---- search dialog ---- */

    openSearch(launcher, returnTarget = launcher) {
      if (!this.searchPanel) return;
      if (this.searchOpening || this.searchPanel.classList.contains('is-open')) return;
      this.closeHarbors({ restore: false });
      this.searchOpening = true;
      this.searchLauncher = launcher;
      this.searchReturnTarget = returnTarget;
      this.searchPanel.hidden = false;
      launcher?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('is-locked');
      this.searchOpenFrame = window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.searchOpening || this.searchPanel.hidden) return;
        this.searchPanel.classList.add('is-open');
        this.searchTrapFrame = window.requestAnimationFrame(() => {
          if (!this.isConnected || !this.searchOpening || !this.searchPanel.classList.contains('is-open')) return;
          this.searchTrap = window.Reefline?.activateModal?.(this.searchPanel, {
            launcher: this.searchReturnTarget,
            initialFocus: 'input[type="search"]'
          });
        });
      });
    }

    closeSearch({ restore = true, immediate = false } = {}) {
      if (!this.searchPanel) return;
      this.searchOpening = false;
      window.cancelAnimationFrame(this.searchOpenFrame);
      window.cancelAnimationFrame(this.searchTrapFrame);
      this.searchOpenFrame = null;
      this.searchTrapFrame = null;
      this.searchPanel.classList.remove('is-open');
      this.searchLauncher?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-locked');
      window.Reefline?.deactivateModal?.(this.searchPanel, { restore });
      this.searchTrap = null;
      const hide = () => {
        if (!this.searchPanel.classList.contains('is-open')) this.searchPanel.hidden = true;
      };
      if (immediate || reducedMotion.matches) hide();
      else window.setTimeout(hide, 300);
    }
  }

  customElements.define('tidal-header', TidalHeader);
})();
